import FormSection from '@/components/Forms/FormSection';
import { intercomTarget } from '@/helpers/intercomTarget';
import usePermissions from '@/hooks/usePermissions';
import { Accordion, Box, Button, FormikTextInput, SimulatedEventPayloadType } from '@palmetto/palmetto-components';
import { Field, FieldArray, useFormikContext } from 'formik';
import { useEffect, useRef, useState } from 'react';

import {
  UserPermissions,
  WaterHeaterEquipmentItem,
  WaterHeaterEquipmentType,
  WaterHeaterFuelType,
  WaterHeaterPreliminaryEquipmentItem,
  WaterHeaterSystem,
  WaterHeaterType,
} from 'types';

import { DiscountsSection } from './DiscountsSection';
import { EquipmentAndMaterialsSection } from './EquipmentAndMaterialsSection';
import { InstallPackageEquipmentSection } from './InstallPackageEquipmentSection';
import { SitePrepSection } from './SitePrepSection';

type WaterHeaterSystemItemForm = Omit<WaterHeaterSystem, 'systemCategory' | 'conditionedArea' | 'equipment'> & {
  systemCategory: string;
  conditionedArea?: number;
  equipment: {
    totalCost?: number;
    items: (WaterHeaterEquipmentItem | WaterHeaterPreliminaryEquipmentItem)[];
  };
};

export const SystemSection = ({
  isInstallPackage = false,
  isPreliminary = false,
  isPackageEditable = true,
}: {
  isInstallPackage?: boolean;
  isPreliminary?: boolean;
  isPackageEditable?: boolean;
}) => {
  const { values, setFieldValue } = useFormikContext<any>();
  const [openPanels, setOpenPanels] = useState<Record<string, boolean>>(() => {
    const initialPanels: Record<string, boolean> = {};
    values?.systems?.forEach((_: any, index: number) => {
      initialPanels[(index + 1).toString()] = true;
    });
    return initialPanels;
  });
  const permissions = usePermissions();
  const userOnlyHasReaderPermission = permissions?.every((permission) =>
    permission.includes(UserPermissions.reader),
  );

  const systemTitleRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const handleAddSystem = (push: (obj: Omit<WaterHeaterSystemItemForm, 'efficiencies'>) => void) => {
    const closeExistingPanels = Object.keys(openPanels).reduce((acc, key) => {
      return { ...acc, [key]: false };
    }, {});
    const newSystemIndex = values.systems.length;
    setOpenPanels({ ...closeExistingPanels, [`${newSystemIndex + 1}`]: true });
    push({
      name: `System ${newSystemIndex + 1}`,
      systemCategory: '',
      equipment: {
        totalCost: 0,
        items: isPreliminary
          ? [
              {
                type: WaterHeaterEquipmentType.waterHeater,
                quantity: 1,
                fuelType: '' as unknown as WaterHeaterFuelType,
                waterHeaterType: '' as unknown as WaterHeaterType,
              },
            ]
          : [],
      },
      sitePrep: {
        totalCost: 0,
        items: [],
      },
      discounts: {
        totalCost: 0,
        items: [],
      },
    });

    setTimeout(() => {
      const newSystemTitleRef = systemTitleRefs.current[newSystemIndex];
      if (newSystemTitleRef) {
        newSystemTitleRef.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }, 100);
  };

  useEffect(() => {
    if (values?.systems?.length && isInstallPackage) {
      const initialPanels: Record<string, boolean> = {};
      values.systems.forEach((_: WaterHeaterSystem, index: number) => {
        initialPanels[`${index + 1}`] = true;
      });
      setOpenPanels(initialPanels);
    }
  }, [values?.systems, isInstallPackage]);

  return (
    <Box padding={{ base: '0 2xs' }}>
      <FieldArray
        name="systems"
        // oxlint-disable-next-line typescript/unbound-method
        render={({ push, remove }) => {
          return (
            <Accordion>
              <Box>
                {values?.systems?.map((_system: WaterHeaterSystem, index: number) => {
                  const panelIndex = `${index + 1}`;
                  return (
                    <Accordion.Panel isOpen={openPanels[panelIndex]} key={`system-${index}`}>
                      <Accordion.PanelSummary
                        hasCaret="right"
                        onToggle={() => {
                          setOpenPanels({ ...openPanels, [panelIndex]: !openPanels[panelIndex] });
                        }}
                        isDetailsOpen={openPanels[panelIndex]}
                        padding={{ desktop: !openPanels[panelIndex] ? 'xl 0' : 'xl 0 0 0', base: 'xl lg' }}
                      >
                        <Box>
                          <Box direction="row">
                            <Box direction="row" gap="xs" alignItems="center">
                              <Box
                                fontSize="xl"
                                fontWeight="medium"
                                ref={(el: HTMLDivElement | null) => {
                                  systemTitleRefs.current[index] = el;
                                }}
                              >
                                {values?.systems?.[index]?.name}
                              </Box>
                              {values?.systems?.length > 1 ? (
                                <Button
                                  variant="tertiary"
                                  tone="neutral"
                                  size="sm"
                                  isDisabled={!isPackageEditable}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    const confirmed = window.confirm('Are you sure you want to remove this system?');
                                    if (!confirmed) return;
                                    remove(index);
                                  }}
                                  iconPrefix="trash"
                                />
                              ) : null}
                            </Box>
                          </Box>
                        </Box>
                      </Accordion.PanelSummary>
                      <FormSection baseStyle={{ display: 'block' }}>
                        <Box
                          direction="row"
                          justifyContent="space-between"
                          gap="lg"
                          display="block"
                          id={`system-${index}`}
                        >
                          <Box gap="lg">
                            <Box
                              direction={{ base: 'column', desktop: 'row' }}
                              gap="xl"
                              padding={{ desktop: 'xl 0', base: '0 lg' }}
                              style={{ flexWrap: 'wrap' }}
                            >
                              <Field
                                label="Name"
                                name={`systems.${index}.name`}
                                id={`systems.${index}.name`}
                                component={FormikTextInput}
                                defaultValue={`System ${index + 1}`}
                                isDisabled={userOnlyHasReaderPermission || !isPackageEditable}
                                width={{ tablet: '4xl' }}
                              />
                            </Box>
                            {!isInstallPackage ? (
                              <EquipmentAndMaterialsSection
                                systemIndex={index}
                                isPreliminary={isPreliminary}
                                helpLink="https://hep.palmetto.finance/en/articles/12003806-hep-residential-system-categories"
                                isPackageEditable={isPackageEditable}
                              />
                            ) : (
                              <InstallPackageEquipmentSection
                                systemIndex={index}
                                isPackageEditable={isPackageEditable}
                              />
                            )}
                            <FormSection title="Equipment Total Cost">
                              <Box gap="xl" alignSelf="flex-end">
                                {!isInstallPackage ? (
                                  <Field
                                    name={`systems.${index}.equipment.totalCost`}
                                    id={`systems.${index}.equipment.totalCost`}
                                    component={FormikTextInput}
                                    prefix="$"
                                    width="2xl"
                                    isDisabled={
                                      userOnlyHasReaderPermission ||
                                      !isPackageEditable ||
                                      !values.systems[index].equipment.items?.length
                                    }
                                    onFocus={(event: SimulatedEventPayloadType) => {
                                      if (event?.target?.value === '0') {
                                        setFieldValue(`systems.${index}.equipment.totalCost`, '');
                                      }
                                    }}
                                    onBlur={(event: SimulatedEventPayloadType) => {
                                      const value = parseFloat(event?.target?.value);
                                      setFieldValue(
                                        `systems.${index}.equipment.totalCost`,
                                        isNaN(value) ? 0 : value,
                                      );
                                    }}
                                    onChange={(event: SimulatedEventPayloadType) => {
                                      const value = event?.target?.value;
                                      if (value.endsWith('.')) {
                                        setFieldValue(`systems.${index}.equipment.totalCost`, value);
                                      } else {
                                        const numericValue = Number(value);
                                        if (!isNaN(numericValue)) {
                                          setFieldValue(`systems.${index}.equipment.totalCost`, value);
                                        }
                                      }
                                    }}
                                  />
                                ) : (
                                  <Box fontSize="md" fontWeight="medium">
                                    ${values.systems[index].equipment.totalCost?.toLocaleString()}
                                  </Box>
                                )}
                              </Box>
                            </FormSection>
                            <SitePrepSection
                              systemIndex={index}
                              isInstallPackage={isInstallPackage}
                              isPackageEditable={isPackageEditable}
                            />
                            <DiscountsSection
                              systemIndex={index}
                              isInstallPackage={isInstallPackage}
                              isPackageEditable={isPackageEditable}
                            />
                          </Box>
                        </Box>
                      </FormSection>
                    </Accordion.Panel>
                  );
                })}
                {!isInstallPackage && (
                  <Button
                    variant="secondary"
                    iconPrefix="add"
                    style={{ alignSelf: 'flex-end', marginTop: '16px' }}
                    isDisabled={!isPackageEditable}
                    onClick={() => handleAddSystem(push)}
                    data-intercom-target={intercomTarget('system-design', 'add-system')}
                  >
                    Add System
                  </Button>
                )}
              </Box>
            </Accordion>
          );
        }}
      />
    </Box>
  );
};
